import {
  Children,
  Fragment,
  ComponentProps,
  createElement,
  isValidElement,
  MouseEvent,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useId,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import {
  Divider,
  Button,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Portal,
  makeStyles,
} from '@material-ui/core';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CheckIcon from '@material-ui/icons/Check';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import AddIcon from '@material-ui/icons/Add';
import {
  SidebarItem,
  SidebarDivider,
  SidebarSpace,
  sidebarConfig,
  useSidebarPinState,
  useSidebarOpenState,
} from '@backstage/core-components';
import { useAnalytics } from '@backstage/core-plugin-api';
import {
  normalizeSidebarPath as normalizePath,
  SidebarDropTarget,
  useSidebarFavorites,
} from './SidebarCustomization';
import { SidebarCustomizationPanel } from './SidebarCustomizationPanel';
import { useSidebarDrag } from './useSidebarDrag';
import {
  getSidebarFlyoutPalette,
  SidebarHoverFlyoutPanel,
  useHoverFlyout,
  useSidebarHoverFlyoutStyles,
} from './SidebarHoverFlyout';
import { useSidebarFlyoutCoordinator } from './SidebarFlyout';
import { SIDEBAR_ITEM_LABEL_FONT_SIZE } from '../../theme/make';

const SIDEBAR_MORE_MAX_HEIGHT = 448;

const useStyles = makeStyles(theme => {
  const { color, continuation, hover } = getSidebarFlyoutPalette(theme);

  return {
    paper: {
      display: 'flex',
      flexDirection: 'column',
      width: 240,
      // Cap the height and scroll so the panel has room for many entries
      // without ever running off the screen.
      maxHeight: `min(${SIDEBAR_MORE_MAX_HEIGHT}px, calc(100vh - 24px))`,
      overflow: 'hidden',
    },
    list: {
      // Minimal top padding so the first item lines up with the "More" row —
      // the actionable items sit right next to the cursor, no header in between.
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      padding: theme.spacing(0.5),
    },
    item: {
      '&&': {
        color,
        width: '100%',
        height: 40,
        boxSizing: 'border-box',
        borderLeft: `${sidebarConfig.selectedIndicatorWidth}px solid transparent`,
        borderRadius: 8,
      },
      '&&:hover': {
        backgroundColor: hover,
      },
      "&&[aria-current='page']": {
        backgroundColor: 'transparent',
        borderRadius: 0,
        paddingLeft: sidebarConfig.selectedIndicatorWidth,
        color: `${continuation} !important`,
        borderLeftColor: continuation,
      },
    },
    triggerWrapper: {
      '& button': {
        transition: 'none !important',
      },
    },
    trigger: {
      '&&:hover': {
        backgroundColor: continuation,
        borderRadius: '4px 0 0 4px',
      },
    },
    triggerActive: {
      '&&': {
        backgroundColor: continuation,
        borderRadius: '4px 0 0 4px',
      },
    },
    footer: {
      flexShrink: 0,
      padding: theme.spacing(0.5),
    },
    footerItem: {
      '& .MuiTypography-root': {
        width: 'auto !important',
      },
      '& .MuiButton-label > div:last-child:empty': {
        display: 'none',
      },
    },
    customizationItem: {
      '&&': {
        color,
        width: '100%',
        height: 44,
        minHeight: 44,
        padding: theme.spacing(0, 1),
        borderRadius: 8,
        border: '1px solid transparent',
      },
      '&&:hover': {
        backgroundColor: hover,
      },
      '&&.Mui-focusVisible': {
        backgroundColor: hover,
        borderColor: theme.palette.primary.main,
      },
    },
    customizationIcon: {
      '&&': {
        minWidth: 36,
        color: 'inherit',
      },
    },
    customizationText: {
      minWidth: 0,
      '& .MuiTypography-root': {
        overflow: 'hidden',
        fontSize: 13,
        fontWeight: 600,
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },
    favoriteIcon: {
      flexShrink: 0,
      marginLeft: theme.spacing(1),
      fontSize: 20,
      opacity: 0.35,
    },
    sidebar: {
      '&&': {
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        minHeight: 0,
        width: '100%',
      },
    },
    primary: {
      flex: '1 1 auto',
      minHeight: 48,
      overflowY: 'auto',
      overflowX: 'hidden',
    },
    movable: {
      '&&': { cursor: 'grab', userSelect: 'none' },
    },
    pinnedButton: {
      '&&': {
        color: theme.palette.common.white,
        width: '100%',
        height: 48,
        padding: 0,
        justifyContent: 'flex-start',
        textTransform: 'none',
        borderRadius: 0,
      },
      '&:hover': { background: 'rgba(255, 255, 255, 0.1)' },
      '&.Mui-focusVisible': {
        boxShadow: 'inset 0 0 0 2px rgba(255, 255, 255, 0.8)',
      },
      '&:hover $pinnedGrip, &.Mui-focusVisible $pinnedGrip': { opacity: 0.8 },
    },
    pinnedIcon: {
      width: sidebarConfig.iconContainerWidth,
      marginRight: -theme.spacing(2),
      flexShrink: 0,
      display: 'flex',
      justifyContent: 'center',
      '& svg': { width: 21, height: 21 },
    },
    pinnedText: {
      fontSize: SIDEBAR_ITEM_LABEL_FONT_SIZE,
      fontWeight: 'bold',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      paddingRight: 24,
    },
    pinnedGrip: { position: 'absolute', right: 6, fontSize: 16, opacity: 0.35 },
    collapsedButton: {
      '&&': { justifyContent: 'center' },
      '& $pinnedIcon': { width: 'auto', marginRight: 0 },
    },
    row: { position: 'relative' },
    dragging: { opacity: 0.3 },
    dragInProgress: {
      '&, & *': {
        cursor: 'grabbing !important',
        userSelect: 'none !important',
      },
    },
    dropIndicator: {
      position: 'absolute',
      top: 0,
      left: 8,
      right: 8,
      height: 3,
      borderRadius: 2,
      background: theme.palette.common.white,
      boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.2)',
      pointerEvents: 'none',
      zIndex: 1,
      '&::before': {
        content: '""',
        position: 'absolute',
        left: -3,
        top: -2,
        width: 7,
        height: 7,
        borderRadius: '50%',
        background: 'inherit',
      },
    },
    endSlot: { position: 'relative', minHeight: 16 },
    emptySidebar: {
      margin: theme.spacing(1),
      minHeight: 80,
      border: '1px dashed rgba(255, 255, 255, 0.5)',
      borderRadius: 8,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing(0.5),
      color: theme.palette.common.white,
      fontFamily: theme.typography.fontFamily,
      fontSize: 12,
    },
    dragPreview: {
      position: 'fixed',
      pointerEvents: 'none',
      zIndex: theme.zIndex.tooltip + 1,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.25),
      width: 232,
      boxSizing: 'border-box',
      padding: theme.spacing(1.25),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 10,
      background: theme.palette.background.paper,
      color: theme.palette.text.primary,
      fontFamily: theme.typography.fontFamily,
      fontSize: 14,
      fontWeight: 600,
      boxShadow: theme.shadows[6],
    },
    previewIcon: {
      width: 32,
      height: 32,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      borderRadius: 6,
      background: hover,
      color: theme.palette.primary.main,
      '& svg': { width: 20, height: 20 },
    },
    previewContent: { minWidth: 0 },
    previewTitle: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    previewHint: {
      fontSize: 11,
      fontWeight: 400,
      marginTop: 2,
      color: theme.palette.text.secondary,
    },
    emptyItem: {
      '&&': {
        color: theme.palette.text.secondary,
        minHeight: 48,
        padding: theme.spacing(0, 1.5),
        opacity: 1,
      },
      '& .MuiTypography-root': {
        fontSize: 14,
        fontWeight: 600,
      },
    },
  };
});

type SidebarMoreChildProps = ComponentProps<typeof SidebarItem> & {
  text: string;
  to: string;
};

type SidebarMoreChild = ReactElement<SidebarMoreChildProps>;
type SidebarSection = { items: SidebarMoreChild[]; divider?: ReactNode };

function getSidebarSections(children: ReactNode): SidebarSection[] {
  const sections: SidebarSection[] = [{ items: [] }];
  Children.toArray(children).forEach(child => {
    const [item] = getSidebarMoreChildren(child);
    if (item) {
      sections[sections.length - 1].items.push(item);
    } else if (isValidElement(child) && child.type === SidebarDivider) {
      sections.push({ items: [], divider: child });
    }
  });
  return sections;
}

function getSidebarMoreChildren(children: ReactNode): SidebarMoreChild[] {
  return Children.toArray(children).filter(
    (child): child is SidebarMoreChild =>
      isValidElement<SidebarMoreChildProps>(child) &&
      typeof child.props.text === 'string' &&
      typeof child.props.to === 'string',
  );
}

function getVisibleSidebarChildren(
  children: ReactNode,
  visiblePaths: Set<string>,
): ReactNode[] {
  const visibleChildren = Children.toArray(children).filter(child => {
    if (
      isValidElement<SidebarMoreChildProps>(child) &&
      typeof child.props.text === 'string' &&
      typeof child.props.to === 'string'
    ) {
      return visiblePaths.has(normalizePath(child.props.to));
    }

    return true;
  });

  return visibleChildren.filter((child, index) => {
    if (
      isValidElement<SidebarMoreChildProps>(child) &&
      typeof child.props.text === 'string' &&
      typeof child.props.to === 'string'
    ) {
      return true;
    }

    const hasItemBefore = visibleChildren
      .slice(0, index)
      .some(candidate => getSidebarMoreChildren(candidate).length > 0);
    const hasItemAfter = visibleChildren
      .slice(index + 1)
      .some(candidate => getSidebarMoreChildren(candidate).length > 0);
    return hasItemBefore && hasItemAfter;
  });
}

type DesktopSidebarMoreProps = {
  items: SidebarMoreChild[];
  sections: SidebarSection[];
  favorites: string[];
  getSection: (path: string) => number;
  onMoveFavorite: (path: string, target: SidebarDropTarget) => void;
  onRemoveFavorite: (path: string) => void;
};

const DesktopSidebarMore = ({
  items,
  sections,
  favorites,
  getSection,
  onMoveFavorite,
  onRemoveFavorite,
}: DesktopSidebarMoreProps) => {
  const classes = useStyles();
  const sharedClasses = useSidebarHoverFlyoutStyles();
  const analytics = useAnalytics();
  const { isOpen: sidebarOpen } = useSidebarOpenState();
  const coordinator = useSidebarFlyoutCoordinator();
  const generatedId = useId();
  const itemId = `sidebar-more:${generatedId}`;
  const [localOpen, setLocalOpen] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [focusRequest, setFocusRequest] = useState<{
    path: string;
    pinned: boolean;
  }>();
  const open =
    customizing || (coordinator ? coordinator.activeId === itemId : localOpen);
  const menuId = 'sidebar-more-menu';
  const flyout = useHoverFlyout({
    open,
    menuId,
    triggerSelector: 'button',
    onOpen: () => {
      if (coordinator) {
        coordinator.open(itemId);
      } else {
        setLocalOpen(true);
      }
    },
    onClose: () => {
      if (customizing) {
        return;
      }
      if (coordinator) {
        coordinator.close(itemId);
      } else {
        setLocalOpen(false);
      }
    },
  });
  const finishCustomization = (restoreFocus = true) => {
    setCustomizing(false);
    setFocusRequest(undefined);
    coordinator?.close(itemId);
    setLocalOpen(false);
    if (restoreFocus) {
      flyout.anchorRef.current?.querySelector('button')?.focus();
    }
  };
  const moveFavorite = (path: string, target: SidebarDropTarget) => {
    onMoveFavorite(path, target);
    setFocusRequest({ path, pinned: true });
    // `variant` tells these apart from the BUI sidebar's events in GA4.
    analytics.captureEvent(
      favorites.includes(path) ? 'sidebar_reorder' : 'sidebar_pin',
      path,
      { attributes: { variant: 'legacy' } },
    );
  };
  const removeFavorite = (path: string) => {
    if (favorites.includes(path)) {
      onRemoveFavorite(path);
      setFocusRequest({ path, pinned: false });
      analytics.captureEvent('sidebar_unpin', path, {
        attributes: { variant: 'legacy' },
      });
    }
  };
  const drag = useSidebarDrag({
    enabled: customizing,
    availablePaths: items.map(item => normalizePath(item.props.to)),
    onClose: finishCustomization,
    onDrop: (path, target) => {
      if (target === undefined) {
        removeFavorite(path);
      } else {
        moveFavorite(path, target);
      }
    },
  });

  useLayoutEffect(() => {
    if (!customizing || !focusRequest) return;
    // Moving between panels replaces the control. Keep keyboard users on the
    // same destination without scrolling either panel after a mouse drop.
    if (focusRequest.pinned) {
      Array.from(
        drag.listRef.current?.querySelectorAll<HTMLElement>(
          '[data-sidebar-path]',
        ) ?? [],
      )
        .find(row => row.dataset.sidebarPath === focusRequest.path)
        ?.querySelector('button')
        ?.focus({ preventScroll: true });
    } else {
      Array.from(
        drag.panelRef.current?.querySelectorAll<HTMLElement>(
          '[data-available-path]',
        ) ?? [],
      )
        .find(row => row.dataset.availablePath === focusRequest.path)
        ?.focus({ preventScroll: true });
    }
  }, [customizing, focusRequest, drag.listRef, drag.panelRef]);

  const isDragging = Boolean(drag.preview);
  useEffect(() => {
    if (!isDragging) return undefined;
    const className = classes.dragInProgress;
    document.body.classList.add(className);
    return () => document.body.classList.remove(className);
  }, [isDragging, classes.dragInProgress]);

  const byPath = new Map(
    items.map(item => [normalizePath(item.props.to), item]),
  );
  const favoriteItems = favorites.flatMap(path => {
    const item = byPath.get(path);
    return item ? [item] : [];
  });
  const favoriteGroups = sections.map((_, section) =>
    favoriteItems.filter(
      item => getSection(normalizePath(item.props.to)) === section,
    ),
  );
  const moreItems = items
    .filter(item => !favorites.includes(normalizePath(item.props.to)))
    .sort(
      (a, b) =>
        a.props.text.localeCompare(b.props.text, 'en', {
          sensitivity: 'base',
        }) ||
        normalizePath(a.props.to).localeCompare(
          normalizePath(b.props.to),
          'en',
        ),
    );
  const previewItem = drag.preview ? byPath.get(drag.preview.path) : undefined;
  const removing = Boolean(
    drag.preview &&
    drag.preview.target === undefined &&
    favorites.includes(drag.preview.path),
  );
  let previewHint = 'Drag into the sidebar';
  if (removing) {
    previewHint = 'Release to remove';
  } else if (drag.preview?.target !== undefined) {
    previewHint = favorites.includes(drag.preview.path)
      ? 'Release to reorder'
      : 'Release to pin';
  }
  const renderFavorite = (item: SidebarMoreChild, section: number) => {
    const path = normalizePath(item.props.to);
    if (!customizing) {
      return item;
    }
    return (
      <div key={path} className={classes.row} data-sidebar-path={path}>
        {drag.preview?.target?.section === section &&
          drag.preview.target.before === path && (
            <div
              className={classes.dropIndicator}
              data-testid="sidebar-drop-indicator"
            />
          )}
        <Button
          disableRipple
          aria-label={`Move ${item.props.text}`}
          aria-describedby={`${generatedId}-instructions`}
          title={item.props.text}
          className={`${classes.pinnedButton} ${classes.movable} ${
            sidebarOpen ? '' : classes.collapsedButton
          } ${drag.preview?.path === path ? classes.dragging : ''}`}
          onPointerDown={event => drag.start(event, path, item.props.text)}
          onDragStart={event => event.preventDefault()}
          onKeyDown={event => {
            if (event.key === 'Delete' || event.key === 'Backspace') {
              event.preventDefault();
              removeFavorite(path);
            } else if (
              event.altKey &&
              (event.key === 'ArrowUp' || event.key === 'ArrowDown')
            ) {
              event.preventDefault();
              const paths = favoriteGroups[section].map(favorite =>
                normalizePath(favorite.props.to),
              );
              const index = paths.indexOf(path);
              if (event.key === 'ArrowUp') {
                if (index > 0)
                  moveFavorite(path, { section, before: paths[index - 1] });
                else if (section > 0)
                  moveFavorite(path, { section: section - 1, before: null });
              } else if (index < paths.length - 1) {
                moveFavorite(path, {
                  section,
                  before: paths[index + 2] ?? null,
                });
              } else if (section < sections.length - 1) {
                const first = favoriteGroups[section + 1][0];
                moveFavorite(path, {
                  section: section + 1,
                  before: first ? normalizePath(first.props.to) : null,
                });
              }
            }
          }}
        >
          <span className={classes.pinnedIcon}>
            {createElement(item.props.icon)}
          </span>
          {sidebarOpen && (
            <span className={classes.pinnedText}>{item.props.text}</span>
          )}
          <DragIndicatorIcon className={classes.pinnedGrip} />
        </Button>
      </div>
    );
  };

  const emptyState = (
    <MenuItem className={classes.emptyItem} disabled role="presentation">
      <ListItemIcon className={classes.customizationIcon}>
        <CheckIcon />
      </ListItemIcon>
      <ListItemText primary="All items are pinned" />
    </MenuItem>
  );

  return (
    <div ref={drag.sidebarRef} className={classes.sidebar}>
      <div
        ref={drag.listRef}
        className={classes.primary}
        aria-label="Pinned navigation"
      >
        {sections.map((section, index) => {
          const group = favoriteGroups[index];
          if (!customizing && group.length === 0) return null;
          const hasPreviousItems = favoriteGroups
            .slice(0, index)
            .some(previous => previous.length > 0);
          return (
            <Fragment key={index}>
              {(customizing || hasPreviousItems) && section.divider}
              <div
                data-sidebar-section={index}
                role="group"
                aria-label={`Sidebar section ${index + 1}`}
              >
                {group.map(item => renderFavorite(item, index))}
                {customizing && group.length === 0 && (
                  <div className={classes.emptySidebar}>
                    <AddIcon />
                    {sidebarOpen && <span>Drop items here</span>}
                  </div>
                )}
                {customizing && (
                  <div className={classes.endSlot}>
                    {drag.preview?.target?.section === index &&
                      drag.preview.target.before === null && (
                        <div
                          className={classes.dropIndicator}
                          data-testid="sidebar-drop-indicator"
                        />
                      )}
                  </div>
                )}
              </div>
            </Fragment>
          );
        })}
      </div>
      <SidebarSpace />
      <div
        ref={flyout.anchorRef}
        className={classes.triggerWrapper}
        role="presentation"
        onMouseEnter={() => flyout.show()}
        onMouseLeave={customizing ? undefined : flyout.handleMouseLeave}
        onBlur={customizing ? undefined : flyout.handleBlur}
        onKeyDown={flyout.handleTriggerKeyDown}
      >
        <SidebarItem
          icon={MoreHorizIcon}
          text="More"
          className={[classes.trigger, open ? classes.triggerActive : undefined]
            .filter(Boolean)
            .join(' ')}
          onClick={(event: MouseEvent) => {
            if (customizing) {
              return;
            }
            if (open) {
              flyout.close();
            } else {
              flyout.show(event.detail === 0);
            }
          }}
        />
        {!customizing && (
          <SidebarHoverFlyoutPanel
            controller={flyout}
            placement="right-end"
            className={`${sharedClasses.paper} ${classes.paper}`}
          >
            <MenuList
              id={menuId}
              dense
              className={classes.list}
              variant="menu"
              aria-label="More navigation"
              onKeyDown={event => flyout.handlePanelKeyDown(event)}
            >
              {moreItems.length
                ? moreItems.map(item => (
                    <SidebarItem
                      key={item.key ?? item.props.to}
                      icon={item.props.icon}
                      text={item.props.text}
                      to={item.props.to}
                      className={[item.props.className, classes.item]
                        .filter(Boolean)
                        .join(' ')}
                      role="menuitem"
                      tabIndex={-1}
                      onClick={item.props.onClick}
                    />
                  ))
                : emptyState}
            </MenuList>
            <Divider />
            <div className={classes.footer}>
              <SidebarItem
                icon={StarBorderIcon}
                text="Customize sidebar"
                className={`${classes.item} ${classes.footerItem}`}
                onClick={() => {
                  flyout.clearCloseTimer();
                  setCustomizing(true);
                }}
              />
            </div>
          </SidebarHoverFlyoutPanel>
        )}
      </div>
      {customizing && (
        <SidebarCustomizationPanel
          anchorRef={drag.sidebarRef}
          panelRef={drag.panelRef}
          availableCount={moreItems.length}
          removing={removing}
          instructionsId={`${generatedId}-instructions`}
          id={menuId}
          onDone={() => finishCustomization()}
        >
          <div className={classes.list}>
            {moreItems.length
              ? moreItems.map(item => {
                  const path = normalizePath(item.props.to);
                  return (
                    <MenuItem
                      key={path}
                      component="button"
                      type="button"
                      role="button"
                      disableRipple
                      data-available-path={path}
                      className={`${classes.customizationItem} ${
                        classes.movable
                      } ${drag.preview?.path === path ? classes.dragging : ''}`}
                      aria-label={`Add ${item.props.text} to sidebar`}
                      aria-describedby={`${generatedId}-instructions`}
                      onPointerDown={event =>
                        drag.start(event, path, item.props.text)
                      }
                      onDragStart={event => event.preventDefault()}
                      onClick={() =>
                        moveFavorite(path, {
                          section: sections.length - 1,
                          before: null,
                        })
                      }
                    >
                      <ListItemIcon className={classes.customizationIcon}>
                        {item.props.icon && createElement(item.props.icon)}
                      </ListItemIcon>
                      <ListItemText
                        className={classes.customizationText}
                        primary={item.props.text}
                      />
                      <DragIndicatorIcon className={classes.favoriteIcon} />
                    </MenuItem>
                  );
                })
              : emptyState}
          </div>
        </SidebarCustomizationPanel>
      )}
      {drag.preview && (
        <Portal>
          <div
            className={classes.dragPreview}
            style={{
              left: Math.max(
                8,
                Math.min(drag.preview.x + 16, window.innerWidth - 240),
              ),
              top: Math.max(
                8,
                Math.min(drag.preview.y + 16, window.innerHeight - 76),
              ),
            }}
            aria-hidden="true"
          >
            <span className={classes.previewIcon}>
              {previewItem && createElement(previewItem.props.icon)}
            </span>
            <div className={classes.previewContent}>
              <div className={classes.previewTitle}>{drag.preview.text}</div>
              <div className={classes.previewHint}>{previewHint}</div>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
};

type SidebarMoreProps = PropsWithChildren<{
  defaultFavorites?: string[];
}>;

export const SidebarMore = ({
  children,
  defaultFavorites = [],
}: SidebarMoreProps) => {
  const { isMobile } = useSidebarPinState();
  const sections = getSidebarSections(children);
  const { favorites, getSection, moveFavorite, removeFavorite } =
    useSidebarFavorites(
      defaultFavorites,
      sections.map(section =>
        section.items.map(item => normalizePath(item.props.to)),
      ),
    );
  const items = getSidebarMoreChildren(children);

  if (isMobile) {
    const defaultFavoritePaths = new Set(defaultFavorites.map(normalizePath));
    const defaultItems = getVisibleSidebarChildren(
      children,
      defaultFavoritePaths,
    );
    const defaultMoreItems = items.filter(
      item => !defaultFavoritePaths.has(normalizePath(item.props.to)),
    );

    return (
      <>
        {defaultItems}
        <SidebarSpace />
        {defaultMoreItems}
      </>
    );
  }

  return (
    <DesktopSidebarMore
      items={items}
      sections={sections}
      favorites={favorites}
      getSection={getSection}
      onMoveFavorite={moveFavorite}
      onRemoveFavorite={removeFavorite}
    />
  );
};

export default SidebarMore;
